import {
  Hospital,
  Menu,
  Bell,
  X,
  Maximize,
  Minimize,
  LogOut,
  User,
} from "lucide-react";
import { useState, useEffect } from "react";
import Sidebar from "./sidebar";
import { useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";
import { showSuccessToast, showErrorToast } from "../../../utils/toast-utils";
import { doEmployeeLogout } from "../../../services/employee/employee-auth-service";
import Loader from "../../loader/loader";

const Header = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Scroll shadow effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      const res = await doEmployeeLogout();
      const status = res?.status || res?.data?.status;

      if (status === 200) {
        showSuccessToast("Logout Successful");
        navigate("/employee-login", { replace: true });
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Logout failed");
    } finally {
      setIsLoading(false); // FIXED
    }
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  return (
    <>
      {isLoading && <Loader />}

      {/* Header */}
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-300 border-b border-white/10 
        ${
          scrolled
            ? "bg-[#10103E] backdrop-blur-md shadow-lg"
            : "bg-[#101044]"
        } 
        text-white px-4 md:px-8 py-3 flex justify-between items-center`}
      >
        {/* Left Section */}
        <div
          className="flex items-center gap-3 group cursor-pointer"
          onClick={() => navigate("/employee-dashboard")}
        >
          <div className="bg-blue-500/20 p-2 rounded-lg group-hover:bg-blue-500/30 transition-colors">
            <Hospital className="text-blue-400" size={24} />
          </div>

          <div className="flex flex-col">
            <span className="font-bold text-lg tracking-tight leading-none">
              MediFlow
            </span>
            <span className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">
              Employee Portal
            </span>
          </div>
        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-2 mr-4 border-r border-white/10 pr-4">
            {/* Notifications */}
            <button className="relative p-2 hover:bg-white/10 rounded-full transition-all group">
              <Bell
                size={20}
                className="text-slate-300 group-hover:text-yellow-400"
              />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-[#081a3a]"></span>
            </button>

            {/* Fullscreen */}
            <button
              onClick={toggleFullScreen}
              className="p-2 hover:bg-white/10 rounded-full transition-all text-slate-300"
              title="Toggle Fullscreen"
            >
              {isFullScreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
          </div>

          {/* Profile & Logout */}
          <div className="flex items-center gap-3 pl-2">
            <div className="text-right">
              <p className="text-sm font-medium">Employee User</p>
              <p className="text-[11px] text-slate-400">Staff Account</p>
            </div>

            <div className="h-10 w-10 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-full flex items-center justify-center border-2 border-white/20">
              <User size={20} />
            </div>

            <Button
              onClick={handleLogout}
              className="ml-2 h-9 px-4 bg-red-500/10 hover:bg-red-500 hover:text-white text-red-400 border border-red-500/20 transition-all flex gap-2"
            >
              <LogOut size={16} />
              <span className="text-xs font-semibold">Logout</span>
            </Button>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            className="p-2 hover:bg-white/10"
            onClick={() => setOpen(true)}
          >
            <Menu className="text-white" size={28} />
          </Button>
        </div>
      </header>

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-[#081a3a] text-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out md:hidden border-r border-white/10
        ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Hospital className="text-blue-400" />
            <h2 className="font-bold text-xl tracking-tight text-white">
              MediFlow
            </h2>
          </div>

          <button
            onClick={() => setOpen(false)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto h-[calc(100%-80px)]">
          <Sidebar />
        </div>
      </div>
    </>
  );
};

export default Header;